import { Fragment, useState, useMemo, useReducer, useRef } from 'react';
import { Buffer } from 'buffer';
import Dropzone, { useDropzone } from 'react-dropzone';
import { gql, useQuery, useLazyQuery, useMutation } from '@apollo/client';
import { Box, Typography, Autocomplete, TextField, Grid, FormControl, FormControlLabel, Switch, Button, Tabs, Tab } from '@mui/material';
import { IUnit } from '../../types/unit';

import { sortSuites, numToCurrency, getOutdoorSize } from '../../utils/Functions';
import { useSelector } from 'react-redux';
import { selectProject } from '../../features/project/projectSlice';
import { scanner417Pdf } from './scanner';
import { baseStyle, activeStyle, acceptStyle, rejectStyle } from '../../utils/Constants';
import { useAppDispatch } from '../../app/hooks';
import { showErrorSnackbar } from '../../features/snackbar/snackbarSlice';
import PdfCard from '../common/PdfCard';
import { purchasersReducer } from '../unitMain/assignment/reducers';
import { IIdentification, IPurchaserInfo, IRealtorInfo, IDealOption } from '../../types/CreateDealForm';
import PurchaserForm from '../common/forms/PurchaserForm';
import { IRealtorsArray } from '../../types/CreateDealForm';
import { Container, FlexEnd } from '../../commonStyles';
import { IOption } from '../../types/project';
import { IWishList } from '../../types/worksheet';
import Dropdown from '../common/formControls/Dropdown';
import { showSuccessSnackbar } from '../../features/snackbar/snackbarSlice';
import { useNavigate } from 'react-router-dom';
import LoadingLogo from '../common/LoadingLogo';
import WishlistChoices, { wishlistChoicesReducer, initialWishlistChoice, isWishlistFilled } from './WishlistChoices';
import WishlistOptions from './WishlistOptions';
import RealtorDetails from './RealtorDetails';
import NewRealtor from './NewRealtor';
import RentOptionCard, { setOptionRent } from './RentOptionCard';
import {
  blankPurchaser,
  purchaserFields as sharedPurchaserFields,
  purchaserUpdateRecord,
  purchaserToForm,
  usePurchaserFormHandlers,
  GETPURCHASER,
  UPDATEPURCHASER,
  UPLOADID,
  DELETEID,
} from './purchaserShared';

const CreateWorksheet = () => {
  const project = useSelector(selectProject);
  const storeDispatch = useAppDispatch();
  const navigate = useNavigate();
  const [units, setUnits] = useState<IUnit[]>([]);
  const [unit, setUnit] = useState<IUnit>();
  const [errors, setErrors] = useState<any>(null);
  const [realtors, setRealtors] = useState<IRealtorInfo[]>([]);
  const [realtor, setRealtor] = useState<IRealtorInfo>();
  const [realtorEditing, setRealtorEditing] = useState<boolean>(false);
  const [options, setOptions] = useState<IDealOption[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [tab, setTab] = useState<'unit' | 'wishlist'>('unit');
  const [wishlistChoices, wishlistDispatch] = useReducer(wishlistChoicesReducer, [initialWishlistChoice]);
  const [wishlistErrors, setWishlistErrors] = useState<boolean>(false);
  // Kept apart from the unit's options so switching tabs doesn't carry one tab's choices over to the other
  const [wishlistOptions, setWishlistOptions] = useState<IDealOption[]>([]);
  const [joinWaitlist, setJoinWaitlist] = useState<string[]>([]);
  const wishlistRef = useRef<HTMLDivElement>(null);
  const isWishlist = tab === 'wishlist';
  const [purchasersState, purchasersDispatch] = useReducer(purchasersReducer, [blankPurchaser(project._id)]);
  const [creatingRealtor, setCreatingRealtor] = useState<boolean>(false);
  const [purchaserOptions, setPurchaserOptions] = useState<IPurchaserInfo[]>([]);

  const { isDragActive, isDragAccept, isDragReject } = useDropzone({
    accept: 'image/gif, image/jpeg, image/png, application/pdf',
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  // The project in redux is loaded without its portal settings, but the wishlist follows them
  // (unit type vs. model type fields, whether options can be added or only waitlisted)
  const { data: portalData, loading: portalLoading } = useQuery(PORTAL, { skip: !project._id, variables: { _id: project._id } });
  const portal = portalData?.projectById?.portal;

  const { loading } = useQuery(UNITS, {
    variables: { filter: { project: project._id, statuses: ['HL', 'HL2', 'HL3', 'SHL', 'PL'] } },
    onCompleted: (data) => {
      setUnits(sortSuites([...data.unitMany], 'suite'));
    },
    onError: (err) => {
      console.log(err, 'err');
    },
  });

  const [getRealtors, { loading: realtorLoading }] = useLazyQuery<IRealtorsArray>(REALTORS, {
    onCompleted: (data) => setRealtors(data.realtorMany),
  });

  // Purchasers already in this project, loaded when a purchaser dropdown is first opened
  const [getPurchasers, { loading: purchaserLoading }] = useLazyQuery(PURCHASERS, {
    variables: { filter: { project: project._id } },
    onCompleted: (data) => setPurchaserOptions(data.purchaserMany),
  });
  const [getPurchaser] = useLazyQuery(GETPURCHASER, { fetchPolicy: 'network-only' });
  const [updatePurchaser] = useMutation(UPDATEPURCHASER);
  const [uploadID] = useMutation(UPLOADID);
  const [deleteID] = useMutation(DELETEID);
  const [savingPurchaser, setSavingPurchaser] = useState<number | null>(null);

  // The purchaser's details in the shape the server accepts (no identifications)
  const purchaserFields = (purchaser: any) => sharedPurchaserFields(purchaser, project._id);

  // An existing purchaser has unsaved changes when its details differ from what was loaded/saved, or it has new ID files
  const hasUnsavedChanges = (purchaser: any) =>
    !!purchaser._id &&
    (JSON.stringify(purchaserFields(purchaser)) !== purchaser.savedSnapshot ||
      purchaser.identifications.some((identification: any) => identification.file));

  const loadPurchaser = async (purchaserId: string, index: number) => {
    const { data } = await getPurchaser({ variables: { _id: purchaserId } });
    if (!data?.purchaserById) {
      storeDispatch(showErrorSnackbar('Unable to load this purchaser'));
      return;
    }
    const filled: any = purchaserToForm(data.purchaserById, project._id);
    filled.savedSnapshot = JSON.stringify(purchaserFields(filled));
    purchasersDispatch({ type: 'UPDATEPURCHASER', payload: { index, data: filled } });
  };

  // Fills the purchaser form from an existing purchaser. The server reuses a project purchaser with the same
  // email as-is, so the worksheet is linked to this record rather than creating a duplicate.
  const selectExistingPurchaser = async (purchaserId: string, index: number) => {
    if (purchasersState.some((purchaser: any, purchaserIndex: number) => purchaserIndex !== index && purchaser._id === purchaserId)) {
      storeDispatch(showErrorSnackbar('This purchaser is already on the worksheet'));
      return;
    }
    await loadPurchaser(purchaserId, index);
  };

  // Clears every field, including the selected purchaser's id
  const clearExistingPurchaser = (index: number) => {
    purchasersDispatch({ type: 'UPDATEPURCHASER', payload: { index, data: { ...blankPurchaser(project._id), _id: null } } });
  };

  // Saves the selected purchaser's details and uploads any new ID files to their record
  const saveExistingPurchaser = async (index: number) => {
    const purchaser: any = purchasersState[index];
    if (!purchaser.email || !purchaser.lastName) {
      storeDispatch(showErrorSnackbar('Purchaser email and last name are required'));
      return;
    }
    setSavingPurchaser(index);
    try {
      await updatePurchaser({ variables: { _id: purchaser._id, record: purchaserUpdateRecord(purchaser, project._id) } });
      for (const identification of purchaser.identifications.filter((id: any) => id.file)) {
        await uploadID({ variables: { id: purchaser._id, name: identification.file.name, file: identification.file } });
      }
      await loadPurchaser(purchaser._id, index);
      storeDispatch(showSuccessSnackbar('Purchaser has been updated!'));
    } catch (error: any) {
      storeDispatch(showErrorSnackbar(error.message));
    } finally {
      setSavingPurchaser(null);
    }
  };

  const [createWorksheet] = useMutation(CREATEWORKSHEET, {
    onCompleted: (data) => {
      storeDispatch(showSuccessSnackbar('Worksheet has been created!'));
      navigate(`/${project._id}/dashboard/worksheets`);
    },
    onError: (error) => {
      console.log(error);
      storeDispatch(showErrorSnackbar(error.message));
    },
  });

  const handleDrop = (acceptedFiles: any, index: number) => {
    setImageLoading(true);
    if (acceptedFiles.length === 0) {
      storeDispatch(showErrorSnackbar('This file type is not allowed'));
      return;
    }
    const file = acceptedFiles[0];

    const fileReader: any = new FileReader();
    if (file) {
      fileReader.readAsDataURL(file);
    }
    fileReader.onloadend = async () => {
      let buffer = Buffer.from(fileReader.result.split(',')[1], 'base64');
      let results = file.type === 'image/jpeg' || file.type === 'image/jpg' ? scanner417Pdf(buffer) : null;
      let values: any = {
        identifications: [
          ...purchasersState[index].identifications,
          {
            name: acceptedFiles[0].name,
            file: acceptedFiles[0],
            result: fileReader.result,
          },
        ],
      };
      if (results) {
        values = {
          ...values,
          firstName: results.firstName,
          lastName: results.lastName,
          idExpiry: results.expiryDate,
          dob: results.dob,
          streetAddress: results.streetAddress,
          city: results.city,
          province: results.province,
          postalCode: results.postalCode,
          country: results.country,
          idJurisdiction: 'Ontario',
          idNumber: results.idNumber,
        };
      }

      purchasersDispatch({
        type: 'UPDATEMULTIPLE',
        payload: {
          index: index,
          values: [values],
        },
      });
      setImageLoading(false);
    };
  };

  const deleteImages = async (purchaserIndex: number, title: string, idIndex: number) => {
    const purchaser: any = purchasersState[purchaserIndex];
    const identification: any = purchaser.identifications[idIndex];
    // A saved ID on an existing purchaser is deleted from their record (PdfCard already asked to confirm)
    if (purchaser._id && identification?._id) {
      try {
        await deleteID({ variables: { _id: purchaser._id, documentId: identification._id, deleteFile: true, type: 'identification' } });
        purchasersDispatch({
          type: 'UPDATE',
          payload: {
            index: purchaserIndex,
            field: 'identifications',
            value: purchaser.identifications.filter((id: any, index: number) => index !== idIndex),
          },
        });
      } catch (error: any) {
        storeDispatch(showErrorSnackbar(error.message));
      }
      return;
    }

    let deleteId = purchasersState[purchaserIndex].identifications.filter(
      (identifications: IIdentification, index: number) => idIndex !== index
    );

    purchasersDispatch({ type: 'UPDATE', payload: { index: purchaserIndex, field: 'identifications', value: deleteId } });
  };

  const {
    handleTextInput,
    handleSigningOfficerInput,
    handleDeleteSigningOfficer,
    addSigningOfficer,
    handleDob,
    handleDetails,
    handleStreetAddress,
    handleEndvestorInput,
    handleIdTypeInput,
    handleIdIssuedByInput,
    handleDateChange,
    handleCorpChange,
  } = usePurchaserFormHandlers(purchasersState, purchasersDispatch, setErrors);

  const handleRealtor = (event: any, value: any) => {
    setRealtorEditing(false);
    setRealtor(value);
  };

  const handleRealtorUpdated = (updated: IRealtorInfo) => {
    setRealtor((current) => ({ ...current, ...updated }));
    setRealtors((current) => current.map((option) => (option._id === updated._id ? { ...option, ...updated } : option)));
  };

  const handleSearchRealtor = (event: any, value: any) => {
    getRealtors({ variables: { filter: { disable: false, search: value } } });
  };

  const handleOptionDropdown = (event: any, numIndex: number) => {
    let filteredOption = project.options.filter((option: IOption, index: number) => option.name === event.target.name);
    if (options.some((option: IDealOption) => option.name === event.target.name)) {
      if (event.target.value === '0') {
        // Keep the option around (at 0) when it's being rented, so the rent choice isn't lost
        let removeOption = options
          .map((data: IDealOption) => (data.name === event.target.name && data.rent ? { ...data, purchaseAmount: 0, amount: 0 } : data))
          .filter((data: IDealOption) => data.name !== event.target.name || data.rent);
        setOptions(removeOption);
      } else {
        let optionsArray = options.map((option: IDealOption, index: number) => {
          if (option.name === event.target.name) {
            return {
              ...option,
              purchaseAmount: parseFloat(event.target.value),
              amount: event.target.value * filteredOption[0].price,
            };
          } else return option;
        });
        setOptions(optionsArray);
      }
    } else {
      if (project.options) {
        setOptions([
          ...options,
          {
            name: event.target.name,
            purchaseAmount: parseFloat(event.target.value),
            amount: event.target.value * filteredOption[0].price,
          },
        ]);
      } else return;
    }
  };

  const optionValue = (name: string, type: string) => {
    let filteredOption: any = options.find((option: IDealOption, index: number) => option.name === name);
    if (filteredOption) {
      return filteredOption[type];
    } else {
      return 0;
    }
  };

  // e.g. "Parking x1 - $50,000, Parking - Rent" - an option can be purchased, rented, or both
  const optionSummary = (option: IDealOption) =>
    [
      option.purchaseAmount ? `${option.name} x${option.purchaseAmount} - ${numToCurrency.format(option.amount!)}` : null,
      option.rent ? `${option.name} - Rent` : null,
    ]
      .filter(Boolean)
      .join(', ');

  const handleSubmit = async (event: any) => {
    event.preventDefault();

    if (realtorEditing) {
      storeDispatch(showErrorSnackbar('Please update or cancel your changes to the realtor'));
      return;
    }

    if (creatingRealtor) {
      storeDispatch(showErrorSnackbar('Please create or cancel the new realtor'));
      return;
    }

    if (isWishlist && !isWishlistFilled(wishlistChoices)) {
      setWishlistErrors(true);
      storeDispatch(showErrorSnackbar('Please complete your wishlist choices'));
      wishlistRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // The server reuses an existing purchaser's saved record, so unsaved edits to one would be silently dropped
    const unsavedIndex = purchasersState.findIndex((purchaser: any) => hasUnsavedChanges(purchaser));
    if (unsavedIndex !== -1) {
      storeDispatch(showErrorSnackbar(`Please save or clear your changes to Purchaser ${unsavedIndex + 1}`));
      return;
    }

    // A new purchaser with an email already used in this project would be swapped for that existing purchaser on the
    // server, dropping what was entered here, so have them picked from the dropdown instead
    let projectPurchasers: any[] = purchaserOptions;
    if (!projectPurchasers.length) {
      const { data } = await getPurchasers();
      projectPurchasers = data?.purchaserMany || [];
    }
    const duplicateIndex = purchasersState.findIndex(
      (purchaser: any) =>
        !purchaser._id &&
        purchaser.email &&
        projectPurchasers.some((existing: any) => existing.email?.toLowerCase() === purchaser.email.trim().toLowerCase())
    );
    if (duplicateIndex !== -1) {
      storeDispatch(
        showErrorSnackbar(
          `Purchaser ${duplicateIndex + 1}'s email already belongs to a purchaser in this project. Please select them from the dropdown instead.`
        )
      );
      return;
    }

    let purchasersFormat = purchasersState.map((purchaser: any) => ({
      ...purchaserFields(purchaser),
      // Links a purchaser picked from the dropdown by id rather than by email
      ...(purchaser._id ? { _id: purchaser._id } : {}),
      // Only newly uploaded files; IDs already on an existing purchaser have no file to upload
      identifications: purchaser.identifications
        .filter((identification: any) => identification.file)
        .map((identification: any) => ({ name: identification.file.name, file: identification.file })),
    }));

    createWorksheet({
      variables: {
        project: project._id,
        unit: isWishlist ? null : unit?._id,
        wishlist: isWishlist,
        wishlistChoices: isWishlist ? wishlistChoices : [],
        purchasers: purchasersFormat,
        realtor: realtor ? realtor._id : null,
        options: isWishlist ? wishlistOptions : options,
        joinWaitlist: isWishlist ? joinWaitlist : [],
        notes: notes,
      },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <form onSubmit={handleSubmit}>
        <Typography variant="h2">
          <strong>Create a Worksheet {!isWishlist && unit ? `- Suite ${unit.suite}` : null}</strong>
        </Typography>
        <Tabs value={tab} onChange={(event, value) => setTab(value)} aria-label="worksheet type" sx={{ mt: 1 }}>
          <Tab label="Unit" value="unit" />
          <Tab label="Wishlist" value="wishlist" />
        </Tabs>
        {isWishlist && portalLoading ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <LoadingLogo />
          </Box>
        ) : isWishlist ? (
          <>
            <Container sx={{ mt: 2 }} ref={wishlistRef}>
              <Typography variant="h2">
                <strong>Wishlist</strong>
              </Typography>
              <WishlistChoices
                choices={wishlistChoices}
                dispatch={wishlistDispatch}
                projectId={project._id}
                showUnitTypes={!!portal?.showUnitTypes}
                showErrors={wishlistErrors}
                setShowErrors={setWishlistErrors}
              />
            </Container>
            {project.options?.some((option: IOption) => option.type === 'residential') ? (
              <Container sx={{ mt: 2 }}>
                <Typography variant="h2">
                  <strong>Suite Options</strong>
                </Typography>
                <WishlistOptions
                  project={project}
                  portal={portal}
                  choices={wishlistChoices}
                  options={wishlistOptions}
                  setOptions={setWishlistOptions}
                  joinWaitlist={joinWaitlist}
                  setJoinWaitlist={setJoinWaitlist}
                />
              </Container>
            ) : null}
          </>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Autocomplete
              options={units}
              getOptionLabel={(option: any) => option.suite}
              disableClearable={false}
              freeSolo={false}
              loading={loading}
              value={unit || null}
              isOptionEqualToValue={(option, value) => option === value}
              onChange={(event: any, newValue: any | null) => setUnit(newValue)}
              renderInput={(params) => <TextField required {...params} label="Select Suite" size="small" />}
            />
          </Box>
        )}
        {!isWishlist && unit ? (
          <Container sx={{ mt: 2 }}>
            <Typography variant="h2">
              <strong>Suite Details</strong>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Box>
                Price: <strong>{numToCurrency.format(unit.basePrice)}</strong>
              </Box>
              <Box>
                Level: <strong>{unit.level}</strong>
              </Box>
              <Box>
                Unit Number: <strong>{unit.unit}</strong>
              </Box>
              <Box>
                Unit Type: <strong>{unit.unitType}</strong>
              </Box>
              <Box>
                Model Type: <strong>{unit.modelType}</strong>
              </Box>
              <Box>
                Exposure: <strong>{unit.exposure}</strong>
              </Box>
              <Box>
                Bathrooms: <strong>{unit.bathroom}</strong>
              </Box>
              <Box>
                Size: <strong>{unit.size}</strong>
              </Box>
              <Box>
                Outdoor Size: <strong>{getOutdoorSize(unit.outdoor) || '0'}</strong>
              </Box>
              {unit.rental ? (
                <Box>
                  Rental Guarantee: <strong>{numToCurrency.format(unit.rental)}</strong>
                </Box>
              ) : null}
              {unit.tier ? (
                <Box>
                  Tier: <strong>{unit.tier}</strong>
                </Box>
              ) : null}
            </Box>
          </Container>
        ) : null}
        {purchasersState.map((pInfo: IPurchaserInfo, index: number) => {
          return (
            <Container sx={{ mt: 2 }} key={index}>
              <FlexEnd sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h2">
                  <strong>Purchaser {index + 1}</strong>
                </Typography>
                {pInfo._id ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      disabled={savingPurchaser === index}
                      onClick={() => saveExistingPurchaser(index)}
                    >
                      {savingPurchaser === index ? 'Saving...' : 'Save'}
                    </Button>
                    <Button variant="outlined" size="small" disabled={savingPurchaser === index} onClick={() => clearExistingPurchaser(index)}>
                      Clear
                    </Button>
                  </Box>
                ) : null}
              </FlexEnd>
              <Box sx={{ my: 2 }}>
                <Autocomplete
                  key={`${index}-${pInfo._id || 'new'}`}
                  options={purchaserOptions}
                  loading={purchaserLoading}
                  value={null}
                  getOptionLabel={(option: any) => `${option.firstName || ''} ${option.lastName} - ${option.email}`}
                  isOptionEqualToValue={(option: any, value: any) => option._id === value._id}
                  onOpen={() => (!purchaserOptions.length && !purchaserLoading ? getPurchasers() : null)}
                  onChange={(event: any, value: any) => (value ? selectExistingPurchaser(value._id, index) : null)}
                  renderInput={(params) => <TextField {...params} size="small" label="Select Existing Purchaser in Project" />}
                />
              </Box>
              <Box>
                <Dropzone onDrop={(files) => handleDrop(files, index)} accept="image/jpg, image/jpeg, image/png, application/pdf">
                  {({ getRootProps, getInputProps }) => (
                    <section>
                      <div {...getRootProps({ style })}>
                        <input {...getInputProps()} />
                        <p>Drag and Drop or Upload Images</p>
                      </div>
                    </section>
                  )}
                </Dropzone>
                {imageLoading ? (
                  <Box
                    sx={{
                      textAlign: 'center',
                    }}
                  >
                    <LoadingLogo />
                  </Box>
                ) : (
                  <Grid container spacing={2}>
                    {pInfo.identifications.length > 0
                      ? pInfo.identifications.map((attachment: any, numIndex: number) => {
                          return (
                            <Grid key={index} item lg={3} md={4} sm={6} xs={12}>
                              <PdfCard
                                file={attachment.result || attachment.getUrl}
                                title={attachment.name ? attachment.name : attachment.file.name}
                                id={index}
                                handleDelete={deleteImages}
                                download={true}
                                index={numIndex}
                              />
                            </Grid>
                          );
                        })
                      : null}
                  </Grid>
                )}
              </Box>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={<Switch checked={pInfo.corp} onChange={(e: any) => handleCorpChange(e, index)} />}
                  label={'Corp'}
                />
              </Box>
              <PurchaserForm
                pInfo={pInfo}
                handleTextInput={handleTextInput}
                handleDateChange={handleDateChange}
                handleEndvestorInput={handleEndvestorInput}
                handleIdTypeInput={handleIdTypeInput}
                handleIdIssuedByInput={handleIdIssuedByInput}
                handleStreetAddress={handleStreetAddress}
                handleDetails={handleDetails}
                handleDeleteSigningOfficer={handleDeleteSigningOfficer}
                handleSigningOfficerInput={handleSigningOfficerInput}
                addSigningOfficer={addSigningOfficer}
                handleDob={handleDob}
                type={index}
                controlled={true}
              />
            </Container>
          );
        })}
        <FlexEnd sx={{ my: 2 }}>
          <Button color="primary" variant="contained" onClick={() => purchasersDispatch({ type: 'ADDNEW', payload: project._id })}>
            Add Purchaser
          </Button>
        </FlexEnd>
        <Container>
          <FlexEnd sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h2">
              <strong>Realtor {realtor && !creatingRealtor ? `- ${realtor.fullName}` : null}</strong>
            </Typography>
            {!creatingRealtor ? (
              <Button
                variant="contained"
                size="small"
                disabled={realtorEditing}
                onClick={() => setCreatingRealtor(true)}
              >
                Create New Realtor
              </Button>
            ) : null}
          </FlexEnd>
          {creatingRealtor ? (
            <NewRealtor
              onCreated={(created) => {
                setRealtors((current) => [created, ...current]);
                setRealtor(created);
                setRealtorEditing(false);
                setCreatingRealtor(false);
              }}
              onCancel={() => setCreatingRealtor(false)}
            />
          ) : (
          <>
          <FormControl
            sx={{
              width: '100%',
              '& .MuiFormLabel-asterisk': {
                color: 'red',
              },
            }}
          >
            <Autocomplete
              sx={{
                width: '100%',
                '& .MuiFormLabel-asterisk': {
                  color: 'red',
                },
              }}
              id={'search-realtor'}
              freeSolo={false}
              options={realtors}
              loading={realtorLoading}
              value={realtor || null}
              getOptionLabel={(option: any) => `${option.firstName} ${option.lastName} ${option.email}`}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              onInputChange={(e, value, reason) => {
                handleSearchRealtor(e, value);
              }}
              onChange={handleRealtor}
              renderInput={(params) => (
                <TextField required={false} {...params} size="small" label={'Search By Name or Email'} margin="normal" />
              )}
            />
          </FormControl>
          {realtor ? (
            <RealtorDetails
              key={realtor._id}
              realtor={realtor}
              onUpdated={handleRealtorUpdated}
              editing={realtorEditing}
              setEditing={setRealtorEditing}
            />
          ) : null}
          </>
          )}
        </Container>
        {project.options.length && unit && !isWishlist ? (
          <Container sx={{ mt: 2 }}>
            <Typography variant="h2">
              <strong>Options</strong>
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {project.options
                // A rentable option is offered for rent to every unit of its type, like the broker portal does,
                // while buying it is still limited to the unit types it's allowed for
                .filter((option: any) => option.type === unit.type && (option.allowedUnits.includes(unit.unitType) || option.rent))
                .map((option: IOption, index: number) => {
                  return (
                    <Fragment key={index}>
                      {option.allowedUnits.includes(unit.unitType) ? (
                        <Grid item xs={12} sm={5}>
                          <Dropdown
                            title={`${option.name} - ${numToCurrency.format(option.price)} each`}
                            id={'purchaseAmount'}
                            menuList={['0', '1', '2', '3']}
                            name={option.name}
                            handleSelect={(e: any) => handleOptionDropdown(e, index)}
                            value={optionValue(option.name, 'purchaseAmount')}
                          />
                        </Grid>
                      ) : null}
                      {option.rent ? (
                        <Grid item xs={12} sm={5}>
                          <RentOptionCard
                            name={option.name}
                            rent={!!options.find((o: IDealOption) => o.name === option.name)?.rent}
                            onChange={(rent) => setOptions(setOptionRent(options, option.name, rent))}
                          />
                        </Grid>
                      ) : null}
                    </Fragment>
                  );
                })}
            </Grid>
          </Container>
        ) : null}
        <Container sx={{ mt: 2 }}>
          <Typography variant="h2">
            <strong>Notes</strong>
            <Box sx={{ mt: 2 }}>
              <TextField
                id="notes-multiline"
                label="Add Notes for Admin and Managers"
                multiline
                rows={4}
                fullWidth
                value={notes}
                onChange={(e: any) => setNotes(e.target.value)}
              />
            </Box>
          </Typography>
        </Container>
        <Container sx={{ mt: 2 }}>
          <Typography variant="h2">
            <strong>Summary</strong>
          </Typography>
          <Box sx={{ mt: 2 }}>
            {purchasersState.map((purchaser: IPurchaserInfo) => {
              return (
                <Box sx={{ mb: 2 }}>
                  <Box>
                    {purchaser.firstName} {purchaser.lastName}
                  </Box>
                  <Box>
                    <strong>{purchaser.email}</strong>
                  </Box>
                  <Box>
                    <strong>{purchaser.primaryPhone}</strong>
                  </Box>
                </Box>
              );
            })}
            {realtor ? (
              <Box sx={{ mb: 2 }}>
                <Box>{realtor.fullName}</Box>
                <Box>
                  <strong>{realtor.brokerage}</strong>
                </Box>
                <Box>
                  <strong>{realtor.email}</strong>
                </Box>
                <Box>
                  <strong>{realtor.directPhone}</strong>
                </Box>
              </Box>
            ) : null}
            {isWishlist ? (
              <Box>
                {wishlistChoices.map((choice: IWishList, index: number) => (
                  <Box key={index}>
                    Choice #{index + 1} -{' '}
                    {[choice.unitType, choice.modelTypes.join(', '), choice.exposure, choice.level].filter(Boolean).join(' - ')}
                  </Box>
                ))}
                {wishlistOptions.map((option: IDealOption) => (
                  <Box key={option.name}>{optionSummary(option)}</Box>
                ))}
              </Box>
            ) : unit ? (
              <Box>
                Unit Price - {numToCurrency.format(unit?.basePrice)}
                {options.length ? (
                  <Box>
                    {options.map((option: IDealOption) => {
                      return <Box key={option.name}>{optionSummary(option)}</Box>;
                    })}
                  </Box>
                ) : null}
                <Box>
                  <strong>
                    Total Price -{' '}
                    {numToCurrency.format(
                      options.reduce((a: any, b: any) => {
                        return a + b.amount;
                      }, unit.basePrice)
                    )}
                  </strong>
                </Box>
              </Box>
            ) : null}
          </Box>
        </Container>
        <FlexEnd sx={{ mt: 2 }}>
          <Button type="submit" variant="contained" color="success">
            Create Worksheet
          </Button>
        </FlexEnd>
      </form>
    </Box>
  );
};

const REALTORS = gql`
  query realtorMany($filter: FilterFindManyRealtorInput) {
    realtorMany(filter: $filter, limit: 10000) {
      _id
      email
      firstName
      lastName
      fullName
      brokerage
      streetAddress
      city
      province
      country
      postalCode
      brokeragePhone
      brokerageFax
      directPhone
      disable
    }
  }
`;

const PURCHASERS = gql`
  query purchaserMany($filter: FilterFindManyPurchaserInput) {
    purchaserMany(filter: $filter, limit: 10000) {
      _id
      email
      firstName
      lastName
    }
  }
`;

const PORTAL = gql`
  query projectById($_id: MongoID!) {
    projectById(_id: $_id) {
      _id
      portal {
        _id
        showUnitTypes
        showOptions
        showOptionsPrice
      }
    }
  }
`;

const UNITS = gql`
  query unitMany($filter: FilterFindManyUnitInput) {
    unitMany(filter: $filter, limit: 10000) {
      _id
      suite
      unit
      level
      tier
      modelType
      unitType
      bathroom
      size
      outdoor {
        name
        size
      }
      basePrice
      rental
      status
      exposure
      type
    }
  }
`;

const CREATEWORKSHEET = gql`
  mutation createWorksheetSales(
    $project: MongoID!
    $unit: MongoID
    $wishlist: Boolean
    $wishlistChoices: [NewWishlistInput]
    $joinWaitlist: [String]
    $purchasers: [NewWorksheetPurchaserInput]
    $realtor: MongoID
    $options: [NewOptionInput]
    $notes: String
  ) {
    createWorksheetSales(
      project: $project
      unit: $unit
      wishlist: $wishlist
      wishlistChoices: $wishlistChoices
      joinWaitlist: $joinWaitlist
      purchasers: $purchasers
      realtor: $realtor
      options: $options
      notes: $notes
    ) {
      _id
    }
  }
`;

export default CreateWorksheet;
